import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { CheckCircle2, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

export const ChatMessage = ({ message, onOptionClick, autoSpeak = false }) => {
  // Support both old (sender/message) and new (role/content) field names
  const isBot = message.sender === 'bot' || message.role === 'assistant';
  const messageText = message.message || message.content || '';
  
  // TTS state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoadingTTS, setIsLoadingTTS] = useState(false);
  const audioRef = useRef(null);
  
  // Handle text-to-speech
  const handleSpeak = async () => {
    if (isSpeaking) {
      // Stop speaking
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsSpeaking(false);
      return;
    }
    
    // Build the full text to speak, including menu options if present
    let textToSpeak = messageText || '';
    
    // If this is a menu message, append the options to be read aloud
    if (message.type === 'menu' && message.options && message.options.length > 0) {
      const optionLabels = message.options.map((opt, idx) => {
        const label = typeof opt === 'string' ? opt : (opt.label || opt.text || '');
        return `Option ${idx + 1}: ${label}`;
      }).join('. ');
      
      if (textToSpeak) {
        textToSpeak = `${textToSpeak}. Here are your options: ${optionLabels}`;
      } else {
        textToSpeak = `Here are your options: ${optionLabels}`;
      }
    }
    
    // Don't speak empty messages
    if (!textToSpeak || textToSpeak.length < 5) {
      return;
    }
    
    try {
      setIsLoadingTTS(true);
      
      const response = await api.textToSpeech(textToSpeak, 'nova', 1.0);
      
      if (response.data.success && response.data.audio) {
        // Create audio from base64
        const audioSrc = `data:audio/mp3;base64,${response.data.audio}`;
        
        if (audioRef.current) {
          audioRef.current.src = audioSrc;
          audioRef.current.onended = () => setIsSpeaking(false);
          audioRef.current.onerror = () => {
            setIsSpeaking(false);
            toast.error('Audio playback failed');
          };
          await audioRef.current.play();
          setIsSpeaking(true);
        }
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.error('Failed to generate speech');
    } finally {
      setIsLoadingTTS(false);
    }
  };
  
  // Auto-speak is DISABLED - TTS only plays when user clicks the speaker button
  // This prevents overlapping audio and gives user control
  // The autoSpeak prop is kept for backwards compatibility but ignored
  
  // Remove the auto-speak useEffect - user must click speaker button to hear messages

  return (
    <div className={`flex ${isBot ? 'justify-start' : 'justify-end'}`}>
      {/* Hidden audio element for TTS playback */}
      <audio ref={audioRef} className="hidden" />
      
      <div className={`max-w-[85%] ${isBot ? '' : 'flex flex-row-reverse'}`}>
        <div className={`flex gap-2 ${isBot ? '' : 'flex-row-reverse'}`}>
          {/* Avatar */}
          {isBot && (
            <div className="flex-shrink-0">
              <img 
                src="/amie-icon.jpg" 
                alt="Amie" 
                className="w-8 h-8 rounded-full"
              />
            </div>
          )}
          
          {/* Message Content */}
          <div className="flex flex-col gap-2">
            {/* Text Message */}
            <div
              className={`rounded-lg px-4 py-2 ${
                isBot
                  ? 'bg-muted text-foreground'
                  : 'bg-primary text-primary-foreground'
              }`}
            >
              <div className="flex items-start gap-2">
                <p className="text-sm whitespace-pre-wrap flex-1">{messageText}</p>
                
                {/* Speaker button for bot messages */}
                {isBot && messageText && messageText.length > 10 && message.type !== 'menu' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 flex-shrink-0 hover:bg-background/50"
                    onClick={handleSpeak}
                    disabled={isLoadingTTS}
                    title={isSpeaking ? "Stop speaking" : "Listen to this message"}
                    data-testid="tts-button"
                  >
                    {isLoadingTTS ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : isSpeaking ? (
                      <VolumeX className="h-3 w-3 text-primary" />
                    ) : (
                      <Volume2 className="h-3 w-3" />
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Menu Options */}
            {message.type === 'menu' && message.options && (
              <Card className="p-3">
                <div className="space-y-2">
                  {message.options.map((option) => (
                    <Button
                      key={option.id}
                      variant="outline"
                      className="w-full justify-start text-left h-auto py-2"
                      onClick={() => onOptionClick(option)}
                    >
                      <span className="font-medium mr-2">{option.id}.</span>
                      <span>{option.label}</span>
                    </Button>
                  ))}
                </div>
              </Card>
            )}

            {/* Confirmation Message */}
            {message.type === 'confirmation' && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                <CheckCircle2 className="h-4 w-4" />
                <span>Successfully created</span>
              </div>
            )}

            {/* Link Message - Show clickable link and metadata */}
            {message.type === 'link' && message.metadata && (
              <div className="mt-2">
                {message.metadata.link && (
                  <a 
                    href={message.metadata.link} 
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm font-medium"
                  >
                    {message.metadata.label || 'Click here'}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                )}
                {message.metadata.incidents?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">Incidents: {message.metadata.incidents.join(', ')}</div>
                )}
                {message.metadata.requests?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">Requests: {message.metadata.requests.join(', ')}</div>
                )}
              </div>
            )}

            {/* Live Chat Trigger - Show button to start live chat */}
            {message.type === 'live_chat_trigger' && (
              <div className="mt-3">
                <button 
                  onClick={() => onOptionClick && onOptionClick({ id: 'start_live_chat', label: 'Talk to Support' })}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  Start Live Chat
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};